import {
  Combobox,
  ComboboxInput,
  ComboboxOptions,
  ComboboxOption,
  ComboboxButton,
} from "@headlessui/react";
import { useState, useCallback, useEffect } from "react";
import proj4 from "proj4";
import { Spinner } from "@chakra-ui/react";

proj4.defs(
  "EPSG:27700",
  "+proj=tmerc +lat_0=49 +lon_0=-2 +k=0.9996012717 +x_0=400000 +y_0=-100000 +ellps=airy +towgs84=446.448,-125.157,542.06,0.15,0.247,0.842,-20.489 +units=m +no_defs"
);

let searchTimeout;
const timeoutCooldown = 500; // In ms

export const LocationSearch = ({ initialLocation = "", onChangeLocation }) => {
  const [locationText, setLocationText] = useState(initialLocation);
  const [selected, setSelected] = useState(null);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLocationText(initialLocation);
    setSelected(null);
  }, [initialLocation]);

  const selectValue = (e) => {
    if (!e) {
      return;
    }

    setLocationText(e.name);

    if (onChangeLocation) {
      onChangeLocation(
        `${e.name}${e.place ? `, ${e.place}` : e.county ? `, ${e.county}` : ""}${e.postcode ? `, ${e.postcode}` : ""}`,
        e.location,
        e.postcode
      );
    }
  };

  const findLocations = useCallback(
    (e) => {
      if (loading) {
        return;
      }
      setLocationText(e.target.value);

      if (searchTimeout) {
        clearTimeout(searchTimeout);
      }

      if (!e.target.value || e.target.value === "") {
        setLocations([]);
        return;
      }

      searchTimeout = setTimeout(async () => {
        setLoading(true);
        const baseUrl = `https://api.os.uk/search/names/v1/find?query=${e.target.value}&maxresults=5&fq=LOCAL_TYPE:City LOCAL_TYPE:Other_Settlement LOCAL_TYPE:Hamlet LOCAL_TYPE:Suburban_Area LOCAL_TYPE:Village LOCAL_TYPE:Town LOCAL_TYPE:Postcode LOCAL_TYPE:Named_Road LOCAL_TYPE:Tramway LOCAL_TYPE:Railway_Station`;
        const res = await fetch(
          `${baseUrl}&key=${encodeURIComponent(process.env.NEXT_PUBLIC_OS_API_KEY)}`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
            },
          }
        );

        const body = await res.json();
        const items = [];
        setLoading(false);
        if (!body.results) {
          return;
        }

        for (const item of body.results) {
          const loc = item.GAZETTEER_ENTRY;
          const coords = proj4("EPSG:27700", "EPSG:4326", [
            loc.GEOMETRY_X,
            loc.GEOMETRY_Y,
          ]);
          items.push({
            key: loc.ID,
            name: loc.NAME1,
            postcode: loc.POSTCODE_DISTRICT,
            county: loc.COUNTY_UNITARY,
            region: loc.REGION,
            place: loc.POPULATED_PLACE,
            location: {
              lat: coords[1],
              lng: coords[0],
            },
          });
        }
        setLocations(items);
      }, timeoutCooldown);
    },
    [loading]
  );

  return (
    <Combobox onChange={selectValue} value={locationText}>
      <div className="relative w-full">
        <ComboboxInput
          placeholder="Enter postcode, town or county"
          aria-label="Location"
          onChange={findLocations}
          value={locationText}
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              event.preventDefault();
            }
          }}
          className="w-full flex-1 appearance-none border border-gray-300 placeholder:text-gray-300 p-2 rounded-lg"
        />
        {loading && (
          <ComboboxButton className="absolute inset-y-0 right-0 px-2.5 flex top-0 bottom-0 items-center">
            <Spinner size="xs" className="text-brand" />
          </ComboboxButton>
        )}
      </div>
      {locations.length > 0 && (
        <ComboboxOptions
          anchor="bottom"
          className="w-[var(--input-width)] mt-2 bg-white border border-gray-100 p-2 rounded-lg flex flex-col gap-2"
        >
          {locations.map((location) => (
            <ComboboxOption
              className="rounded-lg flex flex-col px-2 py-1 bg-white hover:bg-gray-50 cursor-pointer transition"
              key={location.key}
              value={location}
            >
              <h5 className="font-bold text-sm">{location.name}</h5>
              <p className="text-xs text-gray-600">
                {location.county
                  ? location.county
                  : location.place
                    ? location.place
                    : ""}
                {location.postcode ? `, ${location.postcode}` : ""}
              </p>
            </ComboboxOption>
          ))}
        </ComboboxOptions>
      )}
    </Combobox>
  );
};
